import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Shield,
  Calculator,
  Target,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Wallet,
  PiggyBank,
  Activity,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { DrillDownModal } from '../components/DrillDownModal';
import { CapacidadeInvestimentoModal } from '../components/CapacidadeInvestimentoModal';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Cores padrão do sistema - Visual moderno verde
const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const DANGER_COLOR = '#EF4444';
const WARNING_COLOR = '#F59E0B';
const SUCCESS_COLOR = '#22C55E';

// Card de indicador reutilizável — layout uniforme em toda a página
const IndicadorCard = ({
  label,
  value,
  valueColor,
  valueClassName = '',
  subtitle,
  icon: Icon,
  iconNode,
  iconColor,
  iconBg,
  borderClass = 'border-zinc-800',
  testId,
  onClick
}) => (
  <Card className={`bg-zinc-900 ${borderClass} min-h-[160px] ${onClick ? 'cursor-pointer hover:border-zinc-600 transition-colors' : ''}`} data-testid={testId} onClick={onClick}>
    <CardContent className="p-6 flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-4">
        <span className="text-zinc-400 text-xs uppercase tracking-wide font-medium leading-snug">
          {label}
        </span>
        <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: iconBg, color: iconColor }}>
          {iconNode || <Icon className="w-5 h-5" style={{ color: iconColor }} />}
        </div>
      </div>
      <p className={`font-bold font-numbers truncate ${valueClassName || 'text-2xl'}`} style={{ color: valueColor }}>
        {value}
      </p>
      <p className="text-xs text-zinc-500 mt-auto pt-3 leading-relaxed line-clamp-2">
        {subtitle}
      </p>
    </CardContent>
  </Card>
);

// Cabeçalho de seção — dá ritmo e organização à página
const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-5">
    <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-400/80">{title}</h2>
    {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
  </div>
);

export const ProjecaoPage = () => {
  const { selectedGym } = useGym();
  const [loading, setLoading] = useState(true);
  const [projecao, setProjecao] = useState(null);
  const [indicadores, setIndicadores] = useState(null);
  const [simuladorOpen, setSimuladorOpen] = useState(false);
  const [capModalOpen, setCapModalOpen] = useState(false);
  const [simulacaoResultado, setSimulacaoResultado] = useState(null);
  const [drillDown, setDrillDown] = useState({ open: false, tipo: null, mes: null, ano: null });
  const openDrill = (tipo, mes, ano) => setDrillDown({ open: true, tipo, mes, ano });
  const [simulacaoForm, setSimulacaoForm] = useState({
    valor_investimento: '',
    prazo_meses: '12',
    taxa_juros_mensal: '0'
  });
  
  // Filtros
  const [filtroPeriodo, setFiltroPeriodo] = useState('12');
  const [janelaMedia, setJanelaMedia] = useState(3);
  const [savingJanela, setSavingJanela] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    
    try {
      setLoading(true);
      const [projecaoRes, indicadoresRes] = await Promise.all([
        api.get(`/projecao-caixa-v2?gym_id=${selectedGym.id}&meses_futuros=${filtroPeriodo}`),
        api.get(`/indicadores-financeiros?gym_id=${selectedGym.id}`)
      ]);
      
      setProjecao(projecaoRes.data);
      setIndicadores(indicadoresRes.data);
      if (projecaoRes.data?.medias_usadas?.janela_meses) {
        setJanelaMedia(projecaoRes.data.medias_usadas.janela_meses);
      }
    } catch (error) {
      toast.error('Erro ao carregar projeção');
    } finally {
      setLoading(false);
    }
  }, [selectedGym, filtroPeriodo]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
    }
  }, [selectedGym, loadData]);

  const handleJanelaChange = async (novaJanela) => {
    if (!selectedGym) return;
    const valor = parseInt(novaJanela, 10);
    setJanelaMedia(valor);
    try {
      setSavingJanela(true);
      await api.put(`/parametros-projecao?gym_id=${selectedGym.id}`, { janela_projecao_meses: valor });
      await loadData();
      toast.success(`Projeção recalculada com média dos últimos ${valor} meses`);
    } catch (error) {
      toast.error('Erro ao atualizar janela da média');
    } finally {
      setSavingJanela(false);
    }
  };

  const handleSimular = async (e) => {
    e.preventDefault();
    
    if (!simulacaoForm.valor_investimento || !simulacaoForm.prazo_meses) {
      toast.error('Preencha valor e prazo');
      return;
    }
    
    try {
      const response = await api.post(`/simular-investimento?gym_id=${selectedGym.id}`, {
        valor_investimento: parseFloat(simulacaoForm.valor_investimento),
        prazo_meses: parseInt(simulacaoForm.prazo_meses),
        taxa_juros_mensal: parseFloat(simulacaoForm.taxa_juros_mensal) || 0
      });
      
      setSimulacaoResultado(response.data);
    } catch (error) {
      toast.error('Erro na simulação');
    }
  };

  const handleContratar = async () => {
    if (!simulacaoResultado) return;
    
    const nome = prompt('Nome do compromisso:', 'Novo Investimento');
    if (!nome) return;
    
    try {
      await api.post(`/simular-investimento/contratar?gym_id=${selectedGym.id}&nome=${encodeURIComponent(nome)}&tipo=investimento`, {
        valor_investimento: simulacaoResultado.simulacao.valor_investimento,
        prazo_meses: simulacaoResultado.simulacao.prazo_meses,
        taxa_juros_mensal: simulacaoResultado.simulacao.taxa_juros_mensal
      });
      
      toast.success('Compromisso criado com sucesso!');
      setSimuladorOpen(false);
      setSimulacaoResultado(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar compromisso');
    }
  };

  const getSemaforoColor = (semaforo) => {
    switch (semaforo) {
      case 'verde': return PRIMARY_COLOR;
      case 'amarelo': return WARNING_COLOR;
      case 'vermelho': return DANGER_COLOR;
      default: return '#888';
    }
  };

  const getSemaforoIcon = (semaforo) => {
    switch (semaforo) {
      case 'verde': return <CheckCircle2 className="w-6 h-6" />;
      case 'amarelo': return <AlertTriangle className="w-6 h-6" />;
      case 'vermelho': return <XCircle className="w-6 h-6" />;
      default: return <Activity className="w-6 h-6" />;
    }
  };

  const getVeredictoStyle = (veredito) => {
    switch (veredito) {
      case 'APROVADO': return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50' };
      case 'RISCO': return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/50' };
      case 'REPROVADO': return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50' };
      default: return { bg: 'bg-zinc-800', text: 'text-zinc-400', border: 'border-zinc-700' };
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY_COLOR }}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-14 overflow-x-hidden max-w-[1400px] mx-auto" data-testid="projecao-page">
        {/* Header com saudação e filtros */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Projeção Financeira
            </h1>
            <p className="text-zinc-400 mt-2 text-base">
              Visão futura de caixa, indicadores e simulador de investimentos
            </p>
          </div>
          
          {/* Filtros e Ações */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2" data-testid="janela-media-wrapper" title="Quantos meses anteriores ao mês atual entram na média da projeção">
              <span className="text-xs text-zinc-400 whitespace-nowrap">Média dos últimos</span>
              <select
                value={janelaMedia}
                onChange={(e) => handleJanelaChange(e.target.value)}
                disabled={savingJanela}
                data-testid="janela-media-select"
                className="h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="1">1 mês</option>
                <option value="2">2 meses</option>
                <option value="3">3 meses</option>
                <option value="6">6 meses</option>
                <option value="12">12 meses</option>
              </select>
            </div>
            <select
              value={filtroPeriodo}
              onChange={(e) => setFiltroPeriodo(e.target.value)}
              className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
            >
              <option value="6">6 meses</option>
              <option value="12">12 meses</option>
              <option value="18">18 meses</option>
              <option value="24">24 meses</option>
            </select>
          
            {/* Simulador PMT */}
            <Dialog open={simuladorOpen} onOpenChange={setSimuladorOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="simulador-btn">
                  <Calculator className="w-4 h-4 mr-2" />
                  Simular Investimento
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
                  Simulador de Investimento (PMT)
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSimular} className="space-y-4">
                <button
                  type="button"
                  onClick={() => { setSimuladorOpen(false); setCapModalOpen(true); }}
                  className="w-full text-left p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
                  data-testid="projecao-ver-capacidade"
                >
                  <p className="text-sm text-blue-300 font-medium flex items-center gap-2">
                    <Target className="w-4 h-4" /> Ver como calculamos sua capacidade
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Abra a conta aberta (geração, compromissos, reserva e teste de estresse) e ajuste os parâmetros.</p>
                </button>
                <div>
                  <Label className="text-zinc-300">Valor do Investimento (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={simulacaoForm.valor_investimento}
                    onChange={(e) => setSimulacaoForm({...simulacaoForm, valor_investimento: e.target.value})}
                    placeholder="50.000,00"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Prazo (meses)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="120"
                      value={simulacaoForm.prazo_meses}
                      onChange={(e) => setSimulacaoForm({...simulacaoForm, prazo_meses: e.target.value})}
                      placeholder="12"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Taxa Juros (%/mês)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={simulacaoForm.taxa_juros_mensal}
                      onChange={(e) => setSimulacaoForm({...simulacaoForm, taxa_juros_mensal: e.target.value})}
                      placeholder="1.5"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full"
                  style={{ backgroundColor: SECONDARY_COLOR }}
                >
                  Calcular Parcela
                </Button>
              </form>
              
              {/* Resultado da Simulação */}
              {simulacaoResultado && (
                <div className="mt-6 space-y-4">
                  <div className={`p-4 rounded-lg border ${getVeredictoStyle(simulacaoResultado.veredito).bg} ${getVeredictoStyle(simulacaoResultado.veredito).border}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-lg font-bold ${getVeredictoStyle(simulacaoResultado.veredito).text}`}>
                        {simulacaoResultado.veredito}
                      </span>
                      {simulacaoResultado.veredito === 'APROVADO' && <CheckCircle2 className="w-6 h-6 text-green-400" />}
                      {simulacaoResultado.veredito === 'RISCO' && <AlertTriangle className="w-6 h-6 text-yellow-400" />}
                      {simulacaoResultado.veredito === 'REPROVADO' && <XCircle className="w-6 h-6 text-red-400" />}
                    </div>
                    <p className="text-sm text-zinc-300">{simulacaoResultado.mensagem}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-zinc-800 rounded-lg">
                      <p className="text-xs text-zinc-500">Valor da Parcela</p>
                      <p className="text-lg font-bold text-white font-numbers">
                        {formatCurrency(simulacaoResultado.valor_parcela)}
                      </p>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg">
                      <p className="text-xs text-zinc-500">Valor Total</p>
                      <p className="text-lg font-bold text-white font-numbers">
                        {formatCurrency(simulacaoResultado.valor_total)}
                      </p>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg">
                      <p className="text-xs text-zinc-500">Juros Total</p>
                      <p className="text-lg font-bold font-numbers" style={{ color: DANGER_COLOR }}>
                        {formatCurrency(simulacaoResultado.juros_total)}
                      </p>
                    </div>
                    <div className="p-3 bg-zinc-800 rounded-lg">
                      <p className="text-xs text-zinc-500">Capacidade Mensal</p>
                      <p className="text-lg font-bold font-numbers" style={{ color: PRIMARY_COLOR }}>
                        {formatCurrency(simulacaoResultado.capacidade_mensal)}
                      </p>
                    </div>
                  </div>
                  
                  {simulacaoResultado.veredito !== 'REPROVADO' && (
                    <Button
                      onClick={handleContratar}
                      className="w-full"
                      style={{ backgroundColor: PRIMARY_COLOR }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Contratar este Investimento
                    </Button>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Indicadores Principais */}
        {indicadores && (
          <section>
            <SectionHeader title="Indicadores" subtitle="Diagnóstico atual da sua saúde financeira" />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 lg:gap-6">
              <IndicadorCard
                label="Saúde Financeira"
                value={indicadores.semaforo}
                valueClassName="text-xl capitalize"
                valueColor={getSemaforoColor(indicadores.semaforo)}
                subtitle={indicadores.semaforo_mensagem}
                iconNode={getSemaforoIcon(indicadores.semaforo)}
                iconColor={getSemaforoColor(indicadores.semaforo)}
                iconBg={`${getSemaforoColor(indicadores.semaforo)}20`}
                testId="projecao-card-saude"
              />

              <IndicadorCard
                label="Capacidade de Investir"
                value={formatCurrency(indicadores.capacidade_investir)}
                valueColor={PRIMARY_COLOR}
                subtitle="Clique para ver como calculamos"
                icon={Target}
                iconColor={PRIMARY_COLOR}
                iconBg={`${PRIMARY_COLOR}20`}
                testId="projecao-card-capacidade"
                onClick={() => setCapModalOpen(true)}
              />

              <IndicadorCard
                label="Reserva Necessária"
                value={formatCurrency(indicadores.reserva_necessaria)}
                valueColor="#FFFFFF"
                subtitle={`${indicadores.parametros?.meses_reserva} meses de custos`}
                icon={Shield}
                iconColor={SECONDARY_COLOR}
                iconBg={`${SECONDARY_COLOR}20`}
                testId="projecao-card-reserva"
              />

              <IndicadorCard
                label="Índice de Agressividade"
                value={formatPercent(indicadores.indice_agressividade)}
                valueColor={
                  indicadores.indice_agressividade < 30 ? PRIMARY_COLOR :
                  indicadores.indice_agressividade < 50 ? WARNING_COLOR : DANGER_COLOR
                }
                subtitle="Compromissos / Receita"
                icon={Activity}
                iconColor={DANGER_COLOR}
                iconBg={`${DANGER_COLOR}20`}
                testId="projecao-card-agressividade"
              />
            </div>
          </section>
        )}

        {/* Cards Secundários */}
        {indicadores && (
          <section>
            <SectionHeader title="Médias & Caixa" subtitle="Base histórica que alimenta a projeção" />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 lg:gap-6">
              <IndicadorCard
                label="Caixa Livre"
                value={formatCurrency(indicadores.caixa_livre)}
                valueColor="#FFFFFF"
                subtitle="Disponível para uso"
                icon={Wallet}
                iconColor="#A1A1AA"
                iconBg="rgba(63,63,70,0.6)"
                testId="projecao-card-caixa-livre"
              />

              <IndicadorCard
                label="Média Receitas (6m)"
                value={formatCurrency(indicadores.media_receitas)}
                valueColor={PRIMARY_COLOR}
                subtitle={indicadores.media_despesas_fonte === 'dre_fallback'
                  ? 'Estimativa pelo DRE (sem caixa ainda)'
                  : `Fluxo · base ${indicadores.media_meses_base_receitas || 0}/6 meses`}
                icon={TrendingUp}
                iconColor={PRIMARY_COLOR}
                iconBg={`${PRIMARY_COLOR}20`}
                testId="projecao-card-media-receitas"
              />

              <IndicadorCard
                label="Média Despesas (6m)"
                value={formatCurrency(indicadores.media_despesas_operacionais)}
                valueColor={DANGER_COLOR}
                subtitle={indicadores.media_despesas_fonte === 'dre_fallback'
                  ? 'Estimativa pelo DRE (sem caixa ainda)'
                  : `Fluxo · base ${indicadores.media_meses_base_despesas || 0}/6 meses`}
                icon={TrendingDown}
                iconColor={DANGER_COLOR}
                iconBg={`${DANGER_COLOR}20`}
                testId="projecao-card-media-despesas"
              />

              <IndicadorCard
                label="Saldo Livre por Mês"
                value={formatCurrency((indicadores.media_receitas || 0) - (indicadores.media_despesas_operacionais || 0))}
                valueColor={((indicadores.media_receitas || 0) - (indicadores.media_despesas_operacionais || 0)) >= 0 ? PRIMARY_COLOR : DANGER_COLOR}
                subtitle="Receitas − Despesas (média)"
                icon={PiggyBank}
                iconColor={SECONDARY_COLOR}
                iconBg={`${SECONDARY_COLOR}20`}
                testId="projecao-card-saldo-livre"
              />
            </div>
          </section>
        )}

        {/* Cards de resumo dos 3 cenários */}
        {projecao?.resumo && (
          <section>
            <SectionHeader title="Cenários projetados" subtitle={`Saldo final estimado em ${projecao.periodo?.fim || ''}`} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="projecao-cenarios-cards">
              <IndicadorCard
                label="Cenário Pessimista"
                value={formatCurrency(projecao.resumo.saldo_final_pessimista)}
                valueColor={DANGER_COLOR}
                subtitle="Ideal − 80% da média de despesas pontuais"
                icon={TrendingDown}
                iconColor={DANGER_COLOR}
                iconBg="rgba(239,68,68,0.1)"
                borderClass="border-red-500/30"
                testId="projecao-card-pessimista"
              />
              <IndicadorCard
                label="Cenário Realista"
                value={formatCurrency(projecao.resumo.saldo_final_realista)}
                valueColor={WARNING_COLOR}
                subtitle="Ideal − 40% da média de despesas pontuais"
                icon={Activity}
                iconColor={WARNING_COLOR}
                iconBg="rgba(245,158,11,0.1)"
                borderClass="border-yellow-500/30"
                testId="projecao-card-realista"
              />
              <IndicadorCard
                label="Cenário Ideal"
                value={formatCurrency(projecao.resumo.saldo_final_ideal)}
                valueColor={PRIMARY_COLOR}
                subtitle="Só operacional + parcelas (sem pontuais)"
                icon={TrendingUp}
                iconColor={PRIMARY_COLOR}
                iconBg="rgba(34,197,94,0.1)"
                borderClass="border-green-500/30"
                testId="projecao-card-ideal"
              />
            </div>
          </section>
        )}

        {/* Gráfico de cenários futuros */}
        {projecao?.projecao_mensal && projecao.projecao_mensal.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800" data-testid="projecao-grafico-cenarios-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
                Saldo Futuro por Cenário
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={projecao.projecao_mensal.map(m => ({
                    mes: `${m.mes.slice(0,3)}/${String(m.ano).slice(-2)}`,
                    pessimista: m.saldo_pessimista,
                    realista: m.saldo_realista,
                    ideal: m.saldo_ideal
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="mes" stroke="#888" fontSize={12} />
                    <YAxis stroke="#888" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [formatCurrency(value), '']}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="pessimista" name="Pessimista" stroke={DANGER_COLOR} strokeWidth={2} dot={{ fill: DANGER_COLOR, r: 3 }} />
                    <Line type="monotone" dataKey="realista" name="Realista" stroke={WARNING_COLOR} strokeWidth={2} dot={{ fill: WARNING_COLOR, r: 3 }} />
                    <Line type="monotone" dataKey="ideal" name="Ideal" stroke={PRIMARY_COLOR} strokeWidth={2} dot={{ fill: PRIMARY_COLOR, r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Cenários calculados aplicando os fatores de ajuste (Config → Cenários) sobre as médias do Fluxo.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Projeção Mensal */}
        {projecao && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
                Projeção de Caixa - Próximos 12 Meses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium">Mês</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Receita</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Despesas</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Parcelas</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Saldo Mês</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Acumulado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projecao.projecao_mensal.map((mes, index) => {
                      const despesasMes = (mes.saidas_operacionais || 0) + (mes.saidas_retiradas || 0) + (mes.saidas_pontuais || 0);
                      return (
                      <tr 
                        key={index} 
                        className={`border-b border-zinc-800/50 ${mes.is_passado && !mes.is_base ? 'opacity-60' : ''} ${mes.is_base ? 'bg-amber-500/5' : ''} hover:bg-zinc-800/30`}
                        data-testid={`projecao-row-${mes.mes}-${mes.ano}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-white capitalize">{mes.mes}/{mes.ano}</span>
                            {mes.is_base ? (
                              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400" title="Último mês fechado — ponto de partida ancorado no saldo real das contas">Base (fechado)</span>
                            ) : mes.fonte === 'real' ? (
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">Real</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-400">Projetado</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-numbers" style={{ color: PRIMARY_COLOR }}>
                          {mes.fonte === 'real' ? (
                            <button onClick={() => openDrill('recebido_real', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer">
                              {formatCurrency(mes.entradas)}
                            </button>
                          ) : formatCurrency(mes.entradas)}
                        </td>
                        <td className="py-3 px-4 text-right font-numbers" style={{ color: DANGER_COLOR }}>
                          {mes.fonte === 'real' ? (
                            <button onClick={() => openDrill('despesa_operacional', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer">
                              {formatCurrency(despesasMes)}
                            </button>
                          ) : formatCurrency(despesasMes)}
                        </td>
                        <td className="py-3 px-4 text-right font-numbers text-orange-400">
                          {mes.fonte === 'real' && mes.saidas_parcelas > 0 ? (
                            <button onClick={() => openDrill('parcelas', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer">
                              {formatCurrency(mes.saidas_parcelas)}
                            </button>
                          ) : formatCurrency(mes.saidas_parcelas)}
                        </td>
                        <td className={`py-3 px-4 text-right font-bold font-numbers ${mes.saldo_mensal >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(mes.saldo_mensal)}
                        </td>
                        <td className={`py-3 px-4 text-right font-bold font-numbers ${mes.saldo_acumulado >= 0 ? 'text-white' : 'text-red-400'}`}>
                          {formatCurrency(mes.saldo_acumulado)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-zinc-700 bg-zinc-800/50">
                      <td className="py-4 px-4 font-bold text-white">TOTAL</td>
                      <td className="py-4 px-4 text-right font-bold font-numbers" style={{ color: PRIMARY_COLOR }} data-testid="projecao-total-receita">
                        {formatCurrency(projecao.projecao_mensal.reduce((acc, m) => acc + (m.entradas || 0), 0))}
                      </td>
                      <td className="py-4 px-4 text-right font-bold font-numbers" style={{ color: DANGER_COLOR }} data-testid="projecao-total-despesas">
                        {formatCurrency(projecao.projecao_mensal.reduce((acc, m) => acc + (m.saidas_operacionais || 0) + (m.saidas_retiradas || 0) + (m.saidas_pontuais || 0), 0))}
                      </td>
                      <td className="py-4 px-4 text-right font-bold font-numbers text-orange-400" data-testid="projecao-total-parcelas">
                        {formatCurrency(projecao.projecao_mensal.reduce((acc, m) => acc + (m.saidas_parcelas || 0), 0))}
                      </td>
                      <td className="py-4 px-4 text-right font-bold font-numbers" style={{ 
                        color: (projecao.resumo?.saldo_final_projetado || 0) >= 0 ? PRIMARY_COLOR : DANGER_COLOR 
                      }} data-testid="projecao-total-saldo">
                        {formatCurrency(projecao.resumo?.saldo_final_projetado || 0)}
                      </td>
                      <td className="py-4 px-4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {/* Legenda */}
              <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">Real</span>
                  Valores confirmados (banco)
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-400">Projetado</span>
                  Calculado por médias históricas do Fluxo
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Banners informativos no final */}
        {projecao?.medias_usadas?.fonte === 'estimativa_dre' && (
          <div
            className="flex items-start gap-4 p-5 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
            data-testid="projecao-aviso-estimativa-dre"
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: WARNING_COLOR }} />
            <p className="text-sm text-yellow-200 leading-relaxed">
              As médias usadas nesta projeção são estimadas a partir do faturamento e despesas do DRE
              (competência), pois ainda não há movimentações de caixa reais suficientes cadastradas.
              Cadastre suas entradas e saídas em <strong>Caixa</strong> para uma projeção baseada em dinheiro real.
            </p>
          </div>
        )}

        {projecao && (
          <div
            className="flex items-start gap-4 p-5 rounded-lg bg-blue-500/10 border border-blue-500/30"
            data-testid="projecao-banner-fonte"
          >
            <Activity className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-400" />
            <div className="text-sm text-blue-100 space-y-2">
              <p className="font-semibold">Como a projeção é calculada?</p>
              <p className="text-blue-200/80 leading-relaxed">
                Saldo atual das contas + <strong>média dos últimos {projecao.medias_usadas?.janela_meses || janelaMedia} meses anteriores ao mês atual</strong> (recebimento de clientes e despesas operacionais do Fluxo).
                Retiradas de sócio NÃO entram na média (ficam por conta dos cenários). Faturado ainda não recebido (A Receber) e parcelamentos aparecem na aba <strong>Fluxo</strong>. Aportes e empréstimos NÃO entram na média.
              </p>
              {Array.isArray(projecao.medias_usadas?.meses_janela) && projecao.medias_usadas.meses_janela.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="projecao-meses-base">
                  {projecao.medias_usadas.meses_janela.map((m, i) => (
                    <span
                      key={i}
                      className={`text-xs px-2 py-1 rounded-md border ${m.com_dados ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-zinc-700/30 border-zinc-600/40 text-zinc-400'}`}
                      title={m.com_dados ? `Receita: R$ ${m.entradas.toLocaleString('pt-BR')} · Despesa Op.: R$ ${m.saidas_operacionais.toLocaleString('pt-BR')}` : 'Sem movimentação neste mês'}
                    >
                      {m.mes_ano}{m.com_dados ? '' : ' (sem dados)'}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DrillDownModal
          open={drillDown.open}
          onOpenChange={(v) => setDrillDown((d) => ({ ...d, open: v }))}
          gymId={selectedGym?.id}
          tipo={drillDown.tipo}
          mes={drillDown.mes}
          ano={drillDown.ano}
        />

        <CapacidadeInvestimentoModal
          open={capModalOpen}
          onOpenChange={setCapModalOpen}
          gymId={selectedGym?.id}
        />
      </div>
    </DashboardLayout>
  );
};

export default ProjecaoPage;
